import { ElementRef, Injectable, Renderer2 } from '@angular/core';

/**
 * Utility service for DOM manipulations and accessibility attribute management.
 *
 * @remarks
 * Provides methods for generating unique element IDs and manipulating ARIA attributes
 * between DOM elements. Essential for maintaining accessibility compliance when
 * transferring attributes from host components to their internal elements.
 *
 * @example
 *
 * ```typescript
 * @Component({
 *   selector: 'bb-input-text-ui'
 * })
 * export class InputTextComponent {
 *   constructor(private readonly domAttrService: DomAttributesService) {}
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DomAttributesService {
  private idPrefix = 'bb_element_';
  private nextId = 0;

  /**
   * Generates a unique element ID with incrementing counter.
   *
   * @remarks
   * Creates unique identifiers for DOM elements using a prefix and auto-incrementing
   * number. Used for accessibility features like aria-describedby associations
   * and label-to-control relationships.
   *
   * @example
   *
   * ```typescript
   * export class DropdownMultiSelectComponent {
   *   readonly buttonLabelId = this.domAttributesService.generateId();
   *
   *   constructor(private readonly domAttributesService: DomAttributesService) {}
   * }
   * ```
   */
  public generateId() {
    return `${this.idPrefix}${this.nextId++}`;
  }

  /**
   * Moves ARIA attributes from host element to child element and removes them from host.
   *
   * @remarks
   * Transfers all ARIA attributes from a host component element to its internal focusable
   * element, then removes them from the host. Handles the special case of 'arialabel'
   * (without dash) by converting it to proper 'aria-label'. Essential for accessibility
   * when the focusable element is nested inside the component host.
   *
   * @param hostEl - The source element containing ARIA attributes to move
   * @param childEl - The target element that will receive the ARIA attributes
   * @param renderer2 - Angular Renderer2 instance for safe DOM manipulation
   *
   * @example
   *
   * ```typescript
   * // Move ARIA attributes from host to input element
   * this.domAttrService.moveAriaAttributes(
   *   this.elem.nativeElement,
   *   this.inputEl.nativeElement,
   *   this.renderer2
   * );
   * ```
   */
  moveAriaAttributes(hostEl: HTMLElement, childEl: HTMLElement, renderer2: Renderer2) {
    const nodeMap: NamedNodeMap = hostEl.attributes;
    // CUT every aria attribute from the host element and PASTE it into the specified element
    Object.keys(nodeMap)
      .map((key: string) => nodeMap.item(Number(key)))
      .filter((item) => item && (item.name.startsWith('aria-') || item.name.toLowerCase() === 'arialabel'))
      .forEach((item) => {
        const { name: attrName, value: attrVal } = item as Attr;
        if (attrName.toLowerCase() === 'arialabel') {
          renderer2.setAttribute(childEl, 'aria-label', attrVal);
        } else {
          renderer2.setAttribute(childEl, attrName, attrVal);
        }
        renderer2.removeAttribute(hostEl, attrName);
      });
  }

  /**
   * Copies ARIA attributes from host element to child element without removing them from host.
   *
   * @remarks
   * Duplicates all ARIA attributes from a host component element to its internal element
   * while preserving the original attributes on the host. Unlike [moveAriaAttributes]{@link #moveAriaAttributes},
   * this method copies rather than moves the attributes, useful when both elements need
   * the same accessibility information.
   *
   * @param hostEl - The source element containing ARIA attributes to copy
   * @param childEl - The target ElementRef that will receive copies of the ARIA attributes
   * @param renderer2 - Angular Renderer2 instance for safe DOM manipulation
   *
   * @example
   *
   * ```typescript
   * // Copy ARIA attributes while preserving them on host
   * this.domAttrService.copyAriaAttributes(
   *   this.elem.nativeElement,
   *   this.targetElement,
   *   this.renderer2
   * );
   * ```
   */
  copyAriaAttributes(hostEl: HTMLElement, childEl: ElementRef<any>, renderer2: Renderer2) {
    const nodeMap: NamedNodeMap = hostEl.attributes;
    Object.keys(nodeMap)
      .map((key: string) => nodeMap.item(Number(key)))
      .filter((item) => item && item.name.startsWith('aria-'))
      .forEach((item) => item && renderer2.setAttribute(childEl, item.name, item.value));
  }
}
